/* @flow */
/* global window */

import React from 'react';
import Hashids from 'hashids';
import isString from 'lodash/isString';
import { clone, setWith, curry } from 'lodash/fp';

import * as HtmlToReact from 'html-to-react';
import * as d3 from 'd3';
import { fork, take, cancel } from 'redux-saga/effects';

import { defaults } from './constants';

const canvg = require('../extra/canvg');

const htmlToReactParser = new HtmlToReact.Parser(React);

/**
 * Round a number to the given places.
 *
 * @export
 * @param {number | string} value
 * @param {number | string} decimals
 * @returns {number}
 */
export function placeRound(value: number | string, decimals: number | string = defaults.DECIMAL_PLACES): number {
  return +(`${Math.round(+`${value}e+${decimals}`)}e-${decimals}`);
}

/**
 * Calculate the mid point between two angles
 *
 * @export
 * @param {number} startAngle
 * @param {number} endAngle
 * @returns {number}
 */
export function midAngle(startAngle: number, endAngle: number): number {
  return startAngle + ((endAngle - startAngle) / 2);
}

/**
 *
 *
 * @export
 * @param {(number | string)} n
 * @returns {boolean}
 */
export function isNumeric(n: ?(number | string)): boolean {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

/**
 * Calculates the percent of the whole a part is.
 *
 * @export
 * @param {number} part
 * @param {number} whole
 * @returns {number}
 */
export function toPercent(part: number, whole: number): number {
  return (part / whole) * 100;
}

function createMarkup(string: string) {
  try {
    return htmlToReactParser.parse(string);
  } catch (err) {
    return null;
  }
}
/**
 * Create React friendly html element from a string without relying on dangerouslySetInnerHTML
 *
 * @export
 * @param {string} string
 * @returns {HTMLElement}
 */
export function createHTMLMarkup(string?: string, wrapper: string = 'span', attr: string = '') {
  if (!string || (isString(wrapper) && wrapper === '')) return null;
  return createMarkup(`<${wrapper} ${attr}>${string}</${wrapper}>`);
}

/**
 * Create React friendly svg element from a string without relying on dangerouslySetInnerHTML
 *
 * @export
 * @param {string} string
 * @returns {SVGElement}
 */
export function createSVGMarkup(string: string, wrapper: string = 'g', attr?: string = '') {
  if (!string || (isString(wrapper) && wrapper === '')) return null;
  let fixString = string;
  fixString = string.replace(/stroke-width/g, 'strokeWidth');
  fixString = fixString.replace(/font-size/g, 'fontSize');
  fixString = fixString.replace(/font-weight/g, 'fontWeight');
  fixString = fixString.replace(/text-anchor/g, 'textAnchor');
  fixString = fixString.replace(/viewbox/g, 'viewBox');
  return createMarkup(`<${wrapper} ${attr}>${fixString}</${wrapper}>`);
}

/**
 * Check if a value is between two numbers
 *
 * @export
 * @param {number} value
 * @param {number} low
 * @param {number} high
 * @param {boolean} [inclusive=false]
 * @returns {boolean}
 */
export function isBetween(value: number, low: number, high: number, inclusive: boolean = false): boolean {
  const min = Math.min(low, high);
  const max = Math.max(low, high);
  return inclusive ? (value >= min && value <= max) : (value > min && value < max);
}

/**
 * A class to handle unique id generation.
 * https://github.com/ivanakimov/hashids.js
 *
 * @export
 * @class HashId
 */

export class HashId {
  count: number;
  hashids: typeof Hashids;

  constructor() {
    this.hashids = new Hashids('FETBench: SyncBuck', 8,
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890');
    this.count = 0;
  }
  /**
   * Returns the next id
   *
   * @returns {string}
   * @memberOf HashId
   */
  nextId(): string {
    this.count += 1;
    return this.hashids.encode(this.count);
  }

  /**
   * Returns encoded value
   *
   * @returns {string}
   * @memberOf HashId
   */
  encode(value: string | number) {
    return this.hashids.encode(value);
  }
}

/**
 * Detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 * Modified from https://codepen.io/gapcode/pen/vEJNZN
 */
export function isBrowserIE(ua: string = window.navigator.userAgent): boolean {
  /* eslint-disable max-len */
  // IE 10: 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
  // IE 11: 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
  // Edge 12 (Spartan): 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
  // Edge 13: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';
  /* eslint-enable max-len */

  let version = 0;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    version = parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    version = parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    version = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  return version > 0;
}

export function generateBOMCVSBlob(data: Array<Array<string>>): window.Blob {
  const csv = data.map(
    (d: Array<string>) => d.map((f: string) => JSON.stringify(f)).join(','),
  );
  return new window.Blob([csv.join('\r\n')], { type: 'text/csv' });
}

export function downloadBlob(blob: Blob, fileName?: string = 'untitled') {
  // This doesn't allow us to name file.. dumb.
  // const fileURL = URL.createObjectURL(blob);
  // window.open(fileURL);
  // ie11 throws a security error when trying to download blobs like real browers.
  if (isBrowserIE() && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, fileName);
  } else {
    const anchor = window.document.createElement('a');
    anchor.href = window.URL.createObjectURL(blob);
    anchor.download = fileName;

    window.document.body.appendChild(anchor);
    anchor.click();
    window.document.body.removeChild(anchor);
  }
}

export function downloadSVGImage(data: string, fileName: string) {
  const width = 640;
  const height = 350;
  const exportName = `${fileName}.png`;

  const svg = data.replace(/\n\s+/g, '').replace(/&mu;/g, 'μ');

  const canvas = d3.select('body')
    .append('canvas')
    .attr('hidden', true)
    .node();

  const cb = (uri: string) => {
    const a = window.document.createElement('a');
    a.setAttribute('href', uri);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', exportName);

    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const ctx = canvas.getContext('2d', { preserveDrawingBuffer: true });

  canvas.width = width * 2;
  canvas.height = height * 2;

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width * 2, height * 2);

  canvg(canvas, svg, {
    ignoreMouse: true,
    ignoreAnimation: true,
  });

  if ('msToBlob' in canvas) {
    window.navigator.msSaveOrOpenBlob(canvas.msToBlob(), exportName);
    return;
  }

  cb(canvas.toDataURL('image/png'));
}

/**
 * @export
 * @param {string} ua
 * @returns boolean
 */
export function isBrowserFF(ua: string = window.navigator.userAgent): boolean {
  return /Firefox\/([0-9]+)\./.test(ua);
}

/**
 * Join URL paths
 * http://stackoverflow.com/a/29855282
 *
 * @export
 * @param {string[]} parts
 * @param {string} separator
 */
export function pathJoin(parts: any[], separator: string = '/') {
  const replace = new RegExp(`${separator}{1,}`, 'g');

  return parts.join(separator).replace(replace, separator);
}

/**
 * Branch helper function
 * @export
 * @param {boolean} predicate
 * @param {any} Comp1
 * @param {any} Comp2
 * @returns Comp1 || Comp2
 */
export const branch = (predicate: boolean, Comp1: any, Comp2: any) => (predicate ? Comp1 : Comp2);

/**
 * Takes a saga and a callback and spawns a new task
 * that will cancel based on take(cb)
 *
 * @param {Generator} fn
 * @param {Function | [] | string} cb
 * @returns {Generator}
 */
export function* asyncCancelOnTake(fn: Generator<*, *, *>, args: [], cb: Function | [] | string): Generator<*, *, *> {
  const task = yield fork(fn, args);
  yield take(cb);
  yield cancel(task);
}

/**
 * Count decimal places of a given number or string
 * http://stackoverflow.com/a/17369384
 * http://stackoverflow.com/a/10454560
 * @param {number | string} value
 */
export function countDecimals(value: number | string) {
  if (typeof value === 'string') {
    const match = value.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (match) {
      return Math.max(
        0,
        // Number of digits right of decimal point.
        (match[1] ? match[1].length : 0)
        // Adjust for scientific notation.
        - (match[2] ? +match[2] : 0),
      );
    }
  }

  if (typeof value !== 'string') {
    if (Math.floor(value) !== value) {
      return value.toString().split('.')[1].length || 0;
    }
  }

  return 0;
}

/**
 * A curried helper function to set an object path without mutation.
 * This is needed because lodash.set mutates the object.
 *
 * ! Once the project is switched to typesript this will be handled differently.
 *
 * @param {string} path
 * @param {any} value
 * @param {Object} obj
 */
// $FlowIgnore
export const setIn = curry((path, value, obj) =>
  setWith(clone, path, value, clone(obj)),
);



// WEBPACK FOOTER //
// ./src/utils/index.js